import {useAuth} from '@apps-common/auth-react';
import {parseReference} from '@apps-common/geocorn-sdk/api/util.js';
import Map from '@apps-common/ui-maps/Map.jsx';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
} from '@mui/material';
import {orange, pink, yellow} from '@mui/material/colors';
import Overlay from '@planet/maps/Overlay.js';
import {array, func, object, oneOfType, string} from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import CollectionLayer from '../Viewer/Map/CollectionLayer.jsx';
import ReservationsOverlay from './ReservationsOverlay.jsx';
import {useGetCollection} from '@api/collections.js';
import {useWithinBoundary} from '@api/features.js';
import {getSelectedStyle} from '@app/styles/mapStyles.js';
import {getHashFromId} from '@app/util/granero.js';
import ChipAndLabel from '@components/common/ChipAndLabel.jsx';
import VisibilityButton from '@components/common/VisibilityButton.jsx';
import ZoomToButton from '@components/common/ZoomToButton.jsx';

const accessibilityZoneStyle = [
  {
    'stroke-color': yellow[500],
    'stroke-width': 2,
    'fill-color': `color-mix(in srgb, color-mix(in srgb, ${yellow[500]}, white 40%), transparent 90%)`,
    'stroke-line-dash': [5, 5],
  },
];

const QuotaManagerMap = ({
  collection,
  selectedFeatureRefs = [],
  zoomFeature,
  onZoomToFeature,
  selectedStyle,
  baseStyle,
  products,
  reservations,
  selectedProduct,
  activeTab,
}) => {
  const mapRef = useRef();
  const {user} = useAuth();

  const [popupElement, setPopupElement] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);
  const [overlayProducts, setSelectedOverlayProducts] = useState([]);
  const [accessibilityZoneVisible, setAccessibilityZoneVisible] =
    useState(true);
  const [featuresToReserveVisible, setFeaturesToReserveVisible] =
    useState(true);
  const [
    featuresOutsideAccessibilityZoneVisible,
    setFeaturesOutsideAccessibilityZoneVisible,
  ] = useState(true);

  const productAccessibilityZone = selectedProduct?.accessibility_zone?.[0];

  const withinBoundaryQuery = useWithinBoundary(
    {
      boundary: productAccessibilityZone,
      ref: collection['pl:ref'],
    },
    {
      enabled: !!productAccessibilityZone,
    }
  );

  const {refsOutsideBoundary} = withinBoundaryQuery.data ?? {};

  const hasFeaturesOutsideAccessibilityZone = refsOutsideBoundary?.length > 0;

  const collectionExtent = useMemo(
    () => collection.extent.spatial.bbox[0],
    [collection]
  );

  // At this time it will be an array of 1
  const accessibilityZoneRef = selectedProduct?.accessibility_zone?.[0];
  const {
    collectionId: accessibilityZoneCollectionId,
    dataset: accessibilityZoneDataset,
  } = accessibilityZoneRef ? parseReference(accessibilityZoneRef) : {};
  const {data: accessibilityZoneData} = useGetCollection(
    {
      collectionId: accessibilityZoneCollectionId,
      dataset: accessibilityZoneDataset,
    },
    {
      enabled: !!accessibilityZoneCollectionId,
    }
  );

  const onClosePopUp = useCallback(() => setPopupPosition(null), []);

  const zoomToExtent = useCallback(extent => {
    const mapView = mapRef.current.getView();

    mapView.fit(extent, {
      padding: [48, 48, 48, 48],
      duration: 500,
    });
  }, []);

  const onLayerClick = (event, features) => {
    const reservationProducts = features
      .map(f => {
        const featureHashId = f.properties_['pl:ref'].split('/').pop();
        const featureReservations = reservations
          .filter(r => r.aoi_ref.endsWith(featureHashId))
          .map(r => r.product_access_id || r.product_id);

        const reservationProducts = products
          .filter(p => featureReservations.includes(p.id))
          .map(p => ({id: p.id, title: p.title}));
        return reservationProducts;
      })
      .flat();

    if (!features.length || !reservationProducts.length) {
      setPopupPosition(null);
      return;
    }

    setSelectedOverlayProducts(reservationProducts);
    setPopupPosition(event.coordinate);
  };

  useEffect(() => {
    if (mapRef?.current) {
      zoomToExtent(collectionExtent);
    }
  }, [mapRef, zoomToExtent, collectionExtent]);

  useEffect(() => {
    if (zoomFeature) {
      zoomToExtent(zoomFeature.bbox);
      if (onZoomToFeature) {
        onZoomToFeature();
      }
    }
  }, [zoomToExtent, zoomFeature, onZoomToFeature]);

  return (
    <>
      <Map
        mapRef={mapRef}
        onZoomToFeatures={() => zoomToExtent(collectionExtent)}
      >
        <CollectionLayer
          collectionId={collection.id}
          token={user.accessToken}
          selectedFeatures={selectedFeatureRefs}
          selectedStyle={selectedStyle}
          style={baseStyle}
          onClick={onLayerClick}
          visible={featuresToReserveVisible}
        />

        {activeTab === 'available' && hasFeaturesOutsideAccessibilityZone && (
          <CollectionLayer
            collectionId={collection.id}
            token={user.accessToken}
            selectedFeatures={withinBoundaryQuery?.data?.refsOutsideBoundary}
            selectedStyle={getSelectedStyle(orange)}
            style={[]} // hide non-selected features
            visible={featuresOutsideAccessibilityZoneVisible}
          />
        )}

        {!!accessibilityZoneRef && (
          <CollectionLayer
            key={getHashFromId(accessibilityZoneCollectionId)}
            collectionId={getHashFromId(accessibilityZoneCollectionId)}
            token={user.accessToken}
            style={accessibilityZoneStyle}
            dataset={accessibilityZoneDataset}
            visible={accessibilityZoneVisible}
          />
        )}

        <Overlay
          element={popupElement}
          position={popupPosition}
          positioning="bottom-right"
        />
      </Map>
      <ReservationsOverlay
        innerRef={setPopupElement}
        onClose={onClosePopUp}
        reservations={overlayProducts}
      />
      <Card sx={{position: 'absolute', top: 8, right: 8, width: 250}}>
        <CardHeader
          title="Legend"
          slotProps={{
            title: {
              variant: 'overline',
            },
          }}
          sx={{paddingBlockEnd: 0, paddingBlockStart: 1}}
        />
        <Divider />
        <CardContent sx={{paddingInline: 0}}>
          <List disablePadding>
            {!!accessibilityZoneRef && (
              <ListItem
                secondaryAction={
                  <>
                    <VisibilityButton
                      isVisible={accessibilityZoneVisible}
                      onClick={() => {
                        setAccessibilityZoneVisible(prevState => !prevState);
                      }}
                    />
                    <ZoomToButton
                      tooltipText="Zoom to collection extent"
                      onClick={() => {
                        zoomToExtent(
                          accessibilityZoneData?.extent?.spatial?.bbox[0]
                        );
                      }}
                    />
                  </>
                }
              >
                <ChipAndLabel
                  color={yellow}
                  sx={{
                    borderStyle: 'dashed',
                    borderColor: yellow[500],
                  }}
                >
                  Accessibility zone
                </ChipAndLabel>
              </ListItem>
            )}
            {activeTab === 'available' && (
              <ListItem
                secondaryAction={
                  <>
                    <VisibilityButton
                      isVisible={featuresToReserveVisible}
                      onClick={() => {
                        setFeaturesToReserveVisible(prevState => !prevState);
                      }}
                    />
                    <ZoomToButton
                      tooltipText="Zoom to collection extent"
                      onClick={() => {
                        zoomToExtent(collectionExtent);
                      }}
                    />
                  </>
                }
              >
                <ChipAndLabel color={pink}>Areas to reserve</ChipAndLabel>
              </ListItem>
            )}
            {activeTab === 'available' &&
              hasFeaturesOutsideAccessibilityZone && (
                <ListItem
                  secondaryAction={
                    <>
                      <VisibilityButton
                        isVisible={featuresOutsideAccessibilityZoneVisible}
                        onClick={() => {
                          setFeaturesOutsideAccessibilityZoneVisible(
                            prevState => !prevState
                          );
                        }}
                      />
                      <ZoomToButton
                        tooltipText="Zoom to collection extent"
                        onClick={() => {
                          zoomToExtent(collectionExtent);
                        }}
                      />
                    </>
                  }
                >
                  <ChipAndLabel color={orange}>
                    Areas outside accessibility zone
                  </ChipAndLabel>
                </ListItem>
              )}
            {activeTab === 'reserved' && (
              <ListItem>
                <ChipAndLabel color="primary">Reserved areas</ChipAndLabel>
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>
    </>
  );
};

QuotaManagerMap.propTypes = {
  collection: object,
  selectedFeatureRefs: array,
  zoomFeature: object,
  onZoomToFeature: func,
  selectedStyle: oneOfType([array, func, object]),
  baseStyle: oneOfType([array, func, object]),
  reservations: array,
  products: array,
  selectedProduct: object,
  activeTab: string,
};

export default QuotaManagerMap;
